import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { CallableApiService } from '../../core/api/callable-api.service';
import type { FitnessPlanProgram } from '../../core/api/callable-api.service';
import { programById } from './fitness-plan.helpers';
import {
  isProgramGenerationActive,
  isProgramGenerationFailed,
  reconcileProgramsWithActiveGenJobs,
} from './plan-program-generation.helpers';
import {
  PlanProgramGenJobService,
  type ProgramGenJob,
  type ProgramGenJobRecord,
} from './plan-program-gen-job.service';

export async function loadReconciledProgramRow(
  api: CallableApiService,
  auth: AuthService,
  genJobs: PlanProgramGenJobService,
  planId: string,
  programId: string,
): Promise<{
  program: FitnessPlanProgram | undefined;
  activeJob: ProgramGenJobRecord | null;
}> {
  const uid = auth.user()?.uid?.trim() ?? '';
  if (!uid) {
    return { program: undefined, activeJob: null };
  }
  const plan = await api.fetchFitnessPlan(planId);
  const jobs = await genJobs.listActiveJobsForPlan(uid, planId);
  const terminalJobs = await genJobs.listTerminalJobsForPlan(uid, planId);
  const activeJob =
    jobs.find((row) => row.programId.trim() === programId) ?? null;
  const row = programById(plan, programId);
  if (!row) {
    return { program: undefined, activeJob };
  }
  const linkedJobs = new Map<string, ProgramGenJob>();
  const linkedJobId = row.generationJobId?.trim();
  if (linkedJobId) {
    const linked = await genJobs.getJob(uid, linkedJobId);
    if (linked) {
      linkedJobs.set(programId, linked);
    }
  }
  const [program] = reconcileProgramsWithActiveGenJobs(
    [row],
    jobs,
    linkedJobs,
    terminalJobs,
  );
  return { program, activeJob };
}

function buildUrl(router: Router, planId: string, programId: string) {
  return router.createUrlTree([
    '/plans',
    planId,
    'programs',
    programId,
    'build',
  ]);
}

function generationFailedUrl(
  router: Router,
  planId: string,
  programId: string,
) {
  return router.createUrlTree([
    '/plans',
    planId,
    'programs',
    programId,
    'generation-failed',
  ]);
}

/** Dedicated failure screen — only for programs with generationStatus error. */
export const planProgramGenerationFailedGuard: CanActivateFn = async () => true;

/** Block entry to program tabs (failed or still generating). */
export const planProgramTabsGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const api = inject(CallableApiService);
  const auth = inject(AuthService);
  const genJobs = inject(PlanProgramGenJobService);
  const planId =
    route.parent?.paramMap.get('planId')?.trim() ??
    route.paramMap.get('planId')?.trim() ??
    '';
  const programId = route.paramMap.get('programId')?.trim() ?? '';
  if (!planId || !programId) {
    return router.parseUrl('/plans');
  }
  try {
    const { program, activeJob } = await loadReconciledProgramRow(
      api,
      auth,
      genJobs,
      planId,
      programId,
    );
    if (!program) {
      return router.createUrlTree(['/plans', planId, 'programs']);
    }
    if (isProgramGenerationFailed(program)) {
      return generationFailedUrl(router, planId, programId);
    }
    if (isProgramGenerationActive(program, activeJob)) {
      return buildUrl(router, planId, programId);
    }
    return true;
  } catch {
    return router.createUrlTree(['/plans', planId, 'programs']);
  }
};

/** Workouts, split, and sessions require a fully generated (or manual) program. */
export const planProgramContentGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const api = inject(CallableApiService);
  const auth = inject(AuthService);
  const genJobs = inject(PlanProgramGenJobService);
  const planId = route.parent?.paramMap.get('planId')?.trim() ?? '';
  const programId = route.parent?.paramMap.get('programId')?.trim() ?? '';
  if (!planId || !programId) {
    return router.parseUrl('/plans');
  }
  try {
    const { program, activeJob } = await loadReconciledProgramRow(
      api,
      auth,
      genJobs,
      planId,
      programId,
    );
    if (!program) {
      return router.createUrlTree(['/plans', planId, 'programs']);
    }
    if (isProgramGenerationFailed(program)) {
      return generationFailedUrl(router, planId, programId);
    }
    if (isProgramGenerationActive(program, activeJob)) {
      return buildUrl(router, planId, programId);
    }
    return true;
  } catch {
    return router.createUrlTree(['/plans', planId, 'programs']);
  }
};
